import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense, useState, useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import GoogleAnalytics from "./components/GoogleAnalytics";
import SEOHead from "./components/SEOHead";
import ThemeProvider from "./components/ThemeProvider";
import FaviconLoader from "./components/FaviconLoader";
import TargetingWrapper from "./components/TargetingWrapper";
import HomePage from "./pages/HomePage";
import CasinoDetail from "./pages/CasinoDetail";
import NotFoundPage from "./pages/NotFoundPage";
import AmpPage from "./pages/AmpPage";
import "./App.css";

// Single lazy entry point for the whole admin section. Webpack groups every
// admin page and the route table (AdminRoutes.jsx) into one chunk; nothing
// admin-related ends up in the public main bundle. Sub-route names like
// "markalar", "asn-hedefleme", "engellenen-ziyaretciler" therefore never leak.
const AdminRoutes = lazy(() => import(/* webpackChunkName: "admin" */ "./admin/AdminRoutes"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const AdminLoadingFallback = () => (
  <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
    <div className="text-zinc-500">Yükleniyor...</div>
  </div>
);

function App() {
  // Admin path is *not* hardcoded in the bundle. We start empty and ask the
  // backend (`/api/admin-config`) for the real path. Until we have it, no
  // admin route is registered, so anyone trying URLs gets a 404.
  const [adminPath, setAdminPath] = useState(process.env.REACT_APP_ADMIN_PATH || "");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/admin-config`);
        if (res.ok) {
          const data = await res.json();
          if (data.admin_path) {
            setAdminPath(data.admin_path);
            window.__ADMIN_PATH__ = data.admin_path;
          }
        }
      } catch (e) {
        /* default empty -> no admin routes mounted */
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-zinc-500">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <ThemeProvider>
      <div className="App min-h-screen">
        <GoogleAnalytics />
        <SEOHead />
        <FaviconLoader />
        <BrowserRouter>
          <Routes>
            {/* Public routes with targeting */}
            <Route path="/" element={
              <TargetingWrapper>
                <HomePage />
              </TargetingWrapper>
            } />
            <Route path="/casino/:id" element={
              <TargetingWrapper>
                <CasinoDetail />
              </TargetingWrapper>
            } />

            <Route path="/amp" element={<AmpPage />} />

            {/* Admin routes — only registered if the backend revealed the path.
                Mounted as a wildcard so all sub-paths route to the lazy chunk. */}
            {adminPath && (
              <Route
                path={`${adminPath}/*`}
                element={
                  <Suspense fallback={<AdminLoadingFallback />}>
                    <AdminRoutes />
                  </Suspense>
                }
              />
            )}

            {/* 404 Route */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </ThemeProvider>
  );
}

export default App;
